import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ColorNeutralDarkGrey, Spacing4, Spacing8, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import type { ChoosableSubscriptionFrequencyGroup, ProductInformationViewModel } from 'autogen/swagger/all';
import { ProductAccordionAnchor } from 'autogen/translation-keys/trans-website-productreview';
import { UrlText } from 'autogen/translation-keys/trans-website-progressivediscount';
import { CampaignInfoBox } from 'components/features/CampaignInfo';
import InlineButton from 'components/generic/InlineButton';
import PriceModule from 'components/generic/PriceModule';
import { mapPriceInfoViewModelToPriceModuleProps } from 'components/generic/PriceModule/components/shared/mapToPriceModuleProps';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import PageTitleNameRendererMapper from 'components/generic/productName/components/PageTitleNameRendererMapper';
import ChanelHorizontalPriceModule from 'components/generic/productPrice/components/ChanelHorizontalPriceModule';
import { useMapPriceInfoToIPriceModule } from 'components/generic/productPrice/components/shared/helpers';
import { Typography } from 'components/generic/typography';
import KlarnaNudgeWidget from 'components/KlarnaNudgeWidget';
import PlusOfferNudge from 'components/matasPlus/PlusOfferNudge';
import { productAccordionAnchorId } from 'components/product/accordion/ProductAccordion';
import CombinedProductActions from 'components/product/pdpProductAction/CombinedProductActions';
import CrossSellProducts from 'components/productRecommendation/components/CrossSellProducts';
import PDPHandle from 'components/progressiveDiscount/pdpHandle';
import StockInfo from 'components/StockInfo';
import PosStoreStock from 'components/StockInfo/components/StoreStock/PosStoreStock';
import { getSelectedGroup } from 'components/subscription/helpers/subscriptionFrequencyHelper';
import { useIsChanel } from 'hooks/user/useIsBrand';
import { verticalEllipsis } from 'modules/helpers/mixins';
import useGA4Tracking from 'modules/tracking/GA4';
import type { TrackingCategory } from 'modules/tracking/UAEventTracking';
import { breakpoint, sizes } from 'variables';
import BundleNotice from './components/BundleNotice';
import CampaignOffer from './components/CampaignOffer';
import CompetitorMessage from './components/CompetitorMessage';
import DeliveryInformationList from './components/DeliveryInformationList';
import ExpectedDelivery from './components/ExpectedDelivery';
import Giftbox from './components/Giftbox';
import GWPOffer from './components/GWPOffer';
import Points from './components/Points';
import PointsChanel from './components/PointsChanel';
import ReviewAnchor from './components/ReviewAnchor';
import SmartBundle from './components/SmartBundle';
import SplashTagLines from './components/SplashTagLines';
import { getSelectedItem } from './components/SubscriptionPriceInfo/components/components/SubscriptionFrequencySelection/shared/helpers';
import SubscriptionPriceInfo from './components/SubscriptionPriceInfo/SubscriptionPriceInfo';
import ChanelVariants from './components/Variants/chanel/ChanelVariants';
import Variants from './components/Variants/Variants';
import WebsaleAvailability from './components/WebsaleAvailability';
import { SelectedFrequencyContext, SubscriptionContext } from './hooks/context';

// #region Style
const Wrapper = styled.div`
    display: grid;
    grid-auto-columns: auto;
    row-gap: ${Spacing24};
`;

const Top = styled.div`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${Spacing8};
`;

const PriceContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing4};
`;

const StyledSplashTagLines = styled(SplashTagLines)`
    display: none;

    ${breakpoint.up(sizes.lg)} {
        display: flex;
    }
`;

const AnchorContainer = styled.div`
    display: flex;
    flex-direction: column; 
    row-gap: ${Spacing16};
`;

const StyledReviewAnchor = styled(ReviewAnchor)`
    color: ${ColorNeutralDarkGrey};
`;

const ActionAndStockContainer = styled.div`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${Spacing16};
`;

const FlexOrderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing24};
    min-width: 0;
`;

const StyledVariants = styled(Variants)<{ $orderFirst?: boolean }>`
    ${({ $orderFirst }) =>
        $orderFirst
            ? css`
                  order: 0;
              `
            : css`
                  ${breakpoint.up(sizes.lg)} {
                      order: 1;
                  }
              `}
`;

const StyledChanelVariants = styled(ChanelVariants)<{ $orderFirst?: boolean }>`
    ${({ $orderFirst }) =>
        $orderFirst
            ? css`
                  order: 0;
              `
            : css`
                  ${breakpoint.up(sizes.lg)} {
                      order: 1;
                  }
              `}
`;

const StyledGWPOffer = styled(GWPOffer)`
    max-width: 480px;
`;

const StyledCrossSellProducts = styled(CrossSellProducts)`
    max-width: 480px;
`;

const StyledSmartBundle = styled(SmartBundle)`
    max-width: 480px;
`;

const StyledGiftbox = styled(Giftbox)`
    max-width: 480px;
`;

const StyledKlarnaNudgeWidget = styled(KlarnaNudgeWidget)`
    padding-top: ${({ theme }) => theme.Spacing8};
    padding-bottom: ${({ theme }) => theme.Spacing8};
`;

const Snippet = styled(Typography.Body)`
    color: ${({ theme }) => theme.ColorTextPrimary};
    ${verticalEllipsis(2)}
`;

// #endregion Style

function getInitialSelectedItem(choosable: ChoosableSubscriptionFrequencyGroup[] | undefined) {
    if (!choosable) return undefined;

    const selectedGroup = getSelectedGroup(choosable);
    const selectedItem = getSelectedItem(selectedGroup?.frequencies || choosable[0].frequencies);

    return selectedItem;
}

export default function MidSection({
    model,
    canSmartBundleBeRequested = false
}: {
    model: ProductInformationViewModel;
    canSmartBundleBeRequested?: boolean;
}): JSX.Element {
    const {
        productId,
        externalProductId,
        linkAction,
        name,
        reviews,
        showReviews,
        priceInfo,
        showPossibleAdditionalDeliveryCost,
        campaignInfo,
        variants,
        favoriteStatusModel,
        activeFromText,
        isWebshopActive,
        isPreorderActive,
        gwpOfferViewModel,
        progressiveDiscountViewModel,
        productPageAction,
        expectedDelivery,
        returnInformationText,
        returnInformationLinkAction,
        crossSellProducts,
        competitorMessage,
        offer,
        isLightPdp = false,
        productButtonContext,
        uaTrackingCategory,
        fromPowerstep,
        addToBasketType,
        customerNotification,
        dataLayerDto,
        gwpOfferDetailsLinkAction,
        posStoreStock,
        splashes,
        makeupInfo,
        revieveVirtualTryOn,
        findationViewModel,
        webSaleAvailabilityStatus,
        klarnaNudgeViewModel,
        matasPlusOfferNudgeModel,
        giftbox,
        snippet
    } = model;
    const { pointInfo, minimumSalesQuantity, isBundle } = priceInfo;
    const { subscriptionPriceInfoViewModel, storeStock, stockStatus } = expectedDelivery || {};
    const {
        isSubscriptionSelected = false,
        subscriptionInfoModel,
        subscriptionPriceInfo
    } = subscriptionPriceInfoViewModel || {};
    const { discountId: subscriptionDiscountId, subscriptionMinimumQuantity: subscriptionMinimumSalesQuantity } =
        subscriptionPriceInfo || {};
    const productActionModel: Omit<IProductActionButton['model'], 'action'> = {
        productId,
        externalProductId,
        productNavigationLink: linkAction,
        minimumSalesQuantity,
        subscriptionMinimumSalesQuantity,
        listName: dataLayerDto.listName,
        fromPowerstep,
        showPowerstepOnAddToBasket: true,
        addToBasketType,
        customerNotification,
        trackingContext: {
            type: productButtonContext,
            eventCategory: uaTrackingCategory as TrackingCategory // TODO don't get category from BE
        },
        subscriptionDiscountId
    };
    const { t } = useTranslation();
    const [subscriptionActive, setSubscriptionActive] = useState(isSubscriptionSelected);
    const [selectedFrequency, setSelectedFrequency] = useState(
        getInitialSelectedItem(subscriptionInfoModel?.frequencyInfo.choosable)
    );
    const formattedPriceInfo = useMapPriceInfoToIPriceModule(priceInfo, showPossibleAdditionalDeliveryCost);
    const isChanel = useIsChanel();
    const GA4Tracking = useGA4Tracking();
    const priceModuleProps = mapPriceInfoViewModelToPriceModuleProps(priceInfo);

    function aboutProductAnchorClick(e: MouseEvent) {
        e.preventDefault();
        document.getElementById(`${productAccordionAnchorId}`)?.scrollIntoView({
            behavior: 'smooth'
        });
        GA4Tracking({
            eventName: 'link_click',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'link_type', param_value: 'internal' },
                { param_name: 'link_sub_type', param_value: 'anchor' },
                { param_name: 'link_text', param_value: `${t(ProductAccordionAnchor)}` }
            ]
        });
    }

    return (
        <SubscriptionContext.Provider value={{ subscriptionActive, setSubscriptionActive }}>
            <SelectedFrequencyContext.Provider value={{ selectedFrequency, setSelectedFrequency }}>
                <Wrapper>
                    <Top>
                        <StyledSplashTagLines splashes={splashes} />

                        <PageTitleNameRendererMapper lines={name.lines} />

                        {snippet && <Snippet size="L">{snippet}</Snippet>}

                        {!isChanel && (
                            <AnchorContainer>
                                <InlineButton
                                    as="a"
                                    href={`#${productAccordionAnchorId}`}
                                    size="M"
                                    fontWeight="semibold"
                                    onClick={(e) => {
                                        aboutProductAnchorClick(e);
                                    }}
                                >
                                    {t(ProductAccordionAnchor)}
                                </InlineButton>

                                {reviews && showReviews && <StyledReviewAnchor reviews={reviews} />}
                            </AnchorContainer>
                        )}
                    </Top>

                    {isWebshopActive ? (
                        <>
                            <FlexOrderContainer>
                                {variants &&
                                    (isChanel ? (
                                        <StyledChanelVariants
                                            variants={variants}
                                            productId={productId}
                                            $orderFirst={!!subscriptionPriceInfoViewModel}
                                        />
                                    ) : (
                                        <StyledVariants
                                            variants={variants}
                                            productId={productId}
                                            externalProductId={externalProductId}
                                            name={name}
                                            makeupInfo={makeupInfo}
                                            revieveVirtualTryOn={revieveVirtualTryOn}
                                            $orderFirst={!!subscriptionPriceInfoViewModel}
                                            findationViewModel={findationViewModel}
                                        />
                                    ))}

                                <PriceContainer>
                                    {offer && <CampaignOffer campaignOffer={offer} />}

                                    {subscriptionPriceInfoViewModel ? (
                                        <SubscriptionPriceInfo model={subscriptionPriceInfoViewModel} />
                                    ) : isChanel ? (
                                        <ChanelHorizontalPriceModule {...{ ...formattedPriceInfo, size: 24 }} />
                                    ) : (
                                        <PriceModule
                                            {...priceModuleProps}
                                            showPossibleAdditionalDeliveryCost={showPossibleAdditionalDeliveryCost}
                                            size="lg"
                                        />
                                    )}

                                    {competitorMessage && <CompetitorMessage competitorMessage={competitorMessage} />}

                                    {!isPreorderActive &&
                                        !customerNotification &&
                                        pointInfo &&
                                        (isChanel ? (
                                            <PointsChanel
                                                points={pointInfo}
                                                overwritePoints={
                                                    subscriptionPriceInfoViewModel?.subscriptionPriceInfo.pointInfo ??
                                                    undefined
                                                }
                                            />
                                        ) : (
                                            <Points
                                                points={pointInfo}
                                                overwritePoints={
                                                    subscriptionPriceInfoViewModel?.subscriptionPriceInfo.pointInfo ??
                                                    undefined
                                                }
                                            />
                                        ))}
                                </PriceContainer>

                                {campaignInfo && <CampaignInfoBox campaignInfo={campaignInfo} productId={productId} />}

                                {matasPlusOfferNudgeModel && <PlusOfferNudge model={matasPlusOfferNudgeModel} />}

                                {progressiveDiscountViewModel && (
                                    <PDPHandle
                                        model={progressiveDiscountViewModel}
                                        translations={{ seeOffer: t(UrlText) }}
                                    />
                                )}
                            </FlexOrderContainer>

                            <ActionAndStockContainer>
                                {isBundle && <BundleNotice minimumSalesQuantity={minimumSalesQuantity} />}

                                {webSaleAvailabilityStatus && (
                                    <WebsaleAvailability availability={webSaleAvailabilityStatus} />
                                )}

                                <CombinedProductActions
                                    action={productPageAction}
                                    productActionModel={productActionModel}
                                    favoriteModel={favoriteStatusModel}
                                />

                                {klarnaNudgeViewModel && !subscriptionActive && (
                                    <StyledKlarnaNudgeWidget {...klarnaNudgeViewModel} />
                                )}

                                {posStoreStock && <PosStoreStock model={posStoreStock} />}

                                {!isLightPdp && (
                                    <>
                                        <StockInfo
                                            storeStock={storeStock ?? undefined}
                                            productStock={stockStatus}
                                            productId={productId}
                                        />

                                        {expectedDelivery && <ExpectedDelivery {...model} />}

                                        <DeliveryInformationList
                                            returnInformationText={returnInformationText}
                                            returnInformationLinkAction={returnInformationLinkAction}
                                        />
                                    </>
                                )}
                            </ActionAndStockContainer>

                            {gwpOfferViewModel && (
                                <StyledGWPOffer
                                    gwpOfferModel={gwpOfferViewModel}
                                    gwpOfferDetailsLinkAction={gwpOfferDetailsLinkAction}
                                />
                            )}

                            {crossSellProducts && crossSellProducts?.length > 0 && (
                                <StyledCrossSellProducts items={crossSellProducts} />
                            )}

                            {giftbox && <StyledGiftbox giftbox={giftbox} />}

                            {canSmartBundleBeRequested && <StyledSmartBundle productId={productId} />}
                        </>
                    ) : (
                        activeFromText && (
                            <Typography.Body size="M" strong>
                                {activeFromText}
                            </Typography.Body>
                        )
                    )}
                </Wrapper>
            </SelectedFrequencyContext.Provider>
        </SubscriptionContext.Provider>
    );
}
